import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_HOMEPAGE_CONTENT,
  HOMEPAGE_PREVIEW_STORAGE_KEY,
  HomepageContent,
} from '../types/homepageContent';
import './Homepage.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [content, setContent] = useState<HomepageContent>(DEFAULT_HOMEPAGE_CONTENT);

  // Simple admin password
  const ADMIN_PASSWORD = 'hitster2024';

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isPreviewMode = searchParams.get('preview') === '1';

    if (isPreviewMode) {
      const rawPreview = localStorage.getItem(HOMEPAGE_PREVIEW_STORAGE_KEY);
      if (rawPreview) {
        try {
          const previewContent = JSON.parse(rawPreview) as HomepageContent;
          setContent(previewContent);
          return;
        } catch {
          // Continue with API content when preview payload is invalid.
        }
      }
    }

    const loadHomepageContent = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/homepage-content`);
        if (!res.ok) return;
        const data = (await res.json()) as HomepageContent;
        setContent(data);
      } catch {
        // Keep default content when API is unavailable.
      }
    };

    loadHomepageContent();
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      localStorage.setItem('hitster-admin', 'true');
      navigate('/spotify-dashboard');
    } else {
      setError('Onjuist wachtwoord');
      setPassword('');
    }
  };

  const navigateToUrl = (url?: string) => {
    if (!url) return;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.location.href = url;
      return;
    }
    navigate(url);
  };

  return (
    <div className="homepage">
      <div className="homepage-hero">
        <div className="hero-content">
          <h1>{content.heroTitle}</h1>
          <p className="tagline">{content.tagline}</p>
        </div>
      </div>

      <div className="homepage-main">
        <section className="intro-section">
          <h2>{content.introTitle}</h2>
          <p>{content.introText}</p>
        </section>

        <section className="features-section">
          <h2>{content.featuresTitle}</h2>
          <div className="features-grid">
            {content.features.map((feature, index) => (
              <div className="feature-card" key={`feature-${index}`}>
                {feature.imageUrl || feature.iconUrl ? (
                  <img
                    src={feature.imageUrl || feature.iconUrl}
                    alt={feature.title}
                    className="feature-card-image"
                  />
                ) : null}
                <span className="feature-icon">{feature.icon}</span>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                {feature.ctaText && feature.ctaUrl ? (
                  <button
                    className="feature-card-cta"
                    onClick={() => navigateToUrl(feature.ctaUrl)}
                  >
                    {feature.ctaText}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <section className="difficulty-section">
          <h2>{content.difficultyTitle}</h2>
          <div className="difficulty-grid">
            {content.difficulties.map((difficulty, index) => {
              const className = ['original', 'pro', 'expert'][index] || 'original';
              return (
                <div className={`difficulty-card ${className}`} key={`difficulty-${index}`}>
                  <h3>{difficulty.name}</h3>
                  <p>{difficulty.description}</p>
                  <span className="tokens">{difficulty.tokens}</span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="join-section">
          <h2>{content.joinTitle}</h2>
          <p>{content.joinText}</p>
          <button 
            className="join-button"
            onClick={() => navigateToUrl(content.joinButtonUrl || '/join')}
          >
            {content.joinButtonText}
          </button>
        </section>

        <section className="admin-section">
          {!showLogin ? (
            <button 
              className="admin-button"
              onClick={() => setShowLogin(true)}
            >
              {content.adminButtonText}
            </button>
          ) : (
            <form onSubmit={handleLogin} className="login-form">
              <h3>Admin Login</h3>
              <div className="form-group">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Wachtwoord"
                  autoFocus
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              <div className="login-buttons">
                <button type="submit" className="login-submit">Inloggen</button>
                <button 
                  type="button" 
                  className="login-cancel"
                  onClick={() => {
                    setShowLogin(false);
                    setPassword('');
                    setError('');
                  }}
                >
                  Annuleren
                </button>
              </div>
            </form>
          )}
        </section>
      </div>

      <footer className="homepage-footer">
        <p>{content.footerText}</p>
      </footer>
    </div>
  );
};

export default Homepage;
